import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { Button } from '@/components/ui/button'
import { Phone, MapPin, MessageSquare, User } from 'lucide-react'
import { useResolvedPlaces } from '@/features/maps/hooks/useResolvedPlaces'
import type { DeepOrder } from '../types'

interface CustomerInfoProps {
  order: DeepOrder
  compact?: boolean
}

export function CustomerInfo({ order, compact = false }: CustomerInfoProps) {
  const { t } = useTranslation('orders')
  const address = order.deliveryAddress

  // Geocode the delivery address placeId to a formatted address
  const placeIds = useMemo(
    () => (order.withDelivery && address?.city ? [address.city] : []),
    [address?.city, order.withDelivery],
  )
  const resolvedPlaces = useResolvedPlaces(placeIds)
  const resolvedAddress = address ? resolvedPlaces[address.city]?.name : undefined

  const handleCall = () => {
    window.location.href = `tel:${order.telephone}`
  }

  if (compact) {
    return (
      <div className="flex items-center gap-2 text-sm text-muted-foreground">
        <Phone className="h-3.5 w-3.5" />
        <span>{order.telephone}</span>
      </div>
    )
  }

  return (
    <div className="space-y-3">
      <h4 className="font-medium text-sm text-muted-foreground uppercase tracking-wide">
        {t('customer.title')}
      </h4>

      {/* Phone */}
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <Phone className="h-4 w-4 text-muted-foreground" />
          <span className="font-medium">{order.telephone}</span>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={handleCall}
          className="h-7"
        >
          {t('actions.call')}
        </Button>
      </div>

      {/* Customer name (from delivery address — shown for delivery and dine-in) */}
      {address?.name && (
        <div className="flex items-center gap-2">
          <User className="h-4 w-4 text-muted-foreground" />
          <span className="font-medium">{address.name}</span>
        </div>
      )}

      {/* Delivery address (geocoded) */}
      {order.withDelivery && address && (
        <div className="flex items-start gap-2">
          <MapPin className="h-4 w-4 text-muted-foreground mt-0.5" />
          <p className="text-sm text-muted-foreground">
            {resolvedAddress ?? address.city}
          </p>
        </div>
      )}

      {/* Table number for dine-in */}
      {order.dinein && order.tableNumber && (
        <div className="flex items-center gap-2">
          <span className="text-sm text-muted-foreground">
            {t('order.tableNumber', { number: order.tableNumber })}
          </span>
        </div>
      )}

      {/* Comments (shown for all order types — delivery, dine-in, takeaway) */}
      {address?.comments && (
        <div className="flex items-start gap-2 ms-6">
          <MessageSquare className="h-4 w-4 text-muted-foreground mt-0.5" />
          <p className="text-sm text-muted-foreground italic">
            {address.comments}
          </p>
        </div>
      )}
    </div>
  )
}
